/**
 * Phase editor — full-page form for creating or editing a training phase.
 *
 * Mirrors the design's "Edit training phase" screen:
 *
 *   • Name / Intent
 *   • Start date / End date / Weeks / Assessment-at-end toggle
 *   • Primary goal / Secondary goal
 *   • Weekly patterns — one or more named patterns (Build week, Deload week,
 *     Volume / Recovery, etc.), each with a 7-day editor
 *   • Week assignment — map each phase-week to one of the patterns
 *   • Linked goals (optional, chip list)
 *   • Cancel / Delete (edit only) / Save
 *
 * On save the backend reconciles planned workout sessions:
 *   • New schedule entries are materialized as planned sessions.
 *   • Removed entries (still in `planned` status) are deleted.
 *   • Completed / in-progress sessions are NEVER touched.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Icon } from '@k-os/ui';
import {
  useCreatePhase,
  usePatchPhase,
  useDeletePhase,
  useWorkoutTemplates,
  type TrainingPhaseDto,
  type WeeklyPattern,
  type WorkoutTemplateDto,
} from '../../../api/health';
import { EXERCISE_TYPE_META } from './atoms';
import styles from './workouts.module.css';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const PHASE_TEMPLATES: Array<{
  id: string;
  name: string;
  desc: string;
  weeks: number;
  primaryGoal?: string;
  secondaryGoal?: string;
  weeklyPatterns: WeeklyPattern[];
  weekAssignment?: string[];
}> = [
  {
    id: 'blank',
    name: 'Blank',
    desc: 'Start from scratch',
    weeks: 6,
    weeklyPatterns: [
      {
        id: 'wp-weekly',
        label: 'Weekly',
        days: DAY_LABELS.map((d) => ({ day: d, session: '' })),
      },
    ],
  },
  {
    id: 'strength-base',
    name: 'Strength foundation',
    desc: 'Build baseline strength over 8 weeks',
    weeks: 8,
    primaryGoal: 'Strength baseline',
    secondaryGoal: 'Habit consistency',
    weeklyPatterns: [
      {
        id: 'wp-weekly',
        label: 'Weekly',
        days: [
          { day: 'Mon', session: 'Upper strength' },
          { day: 'Tue', session: 'Mobility / Z2' },
          { day: 'Wed', session: 'Lower strength' },
          { day: 'Thu', session: 'Rest' },
          { day: 'Fri', session: 'Full body' },
          { day: 'Sat', session: 'Easy run or walk' },
          { day: 'Sun', session: 'Rest' },
        ],
      },
    ],
  },
  {
    id: 'recomp',
    name: 'Recomposition',
    desc: 'Body comp + cardio base. Includes a deload week.',
    weeks: 7,
    primaryGoal: 'Body recomposition',
    secondaryGoal: 'Cardiovascular base',
    weeklyPatterns: [
      {
        id: 'wp-build',
        label: 'Build week',
        days: [
          { day: 'Mon', session: 'Upper strength' },
          { day: 'Tue', session: 'Easy run · Z2' },
          { day: 'Wed', session: 'Rest / mobility' },
          { day: 'Thu', session: 'Lower strength' },
          { day: 'Fri', session: 'Full body hypertrophy' },
          { day: 'Sat', session: 'Long easy run' },
          { day: 'Sun', session: 'Rest' },
        ],
      },
      {
        id: 'wp-deload',
        label: 'Deload week',
        days: [
          { day: 'Mon', session: 'Upper strength · 60%' },
          { day: 'Tue', session: 'Walk 30 min' },
          { day: 'Wed', session: 'Mobility' },
          { day: 'Thu', session: 'Lower strength · 60%' },
          { day: 'Fri', session: 'Rest' },
          { day: 'Sat', session: 'Easy 4km' },
          { day: 'Sun', session: 'Rest' },
        ],
      },
    ],
    weekAssignment: ['wp-build', 'wp-build', 'wp-build', 'wp-deload', 'wp-build', 'wp-build', 'wp-build'],
  },
];

function newPatternId(): string {
  return 'wp-' + Math.random().toString(36).slice(2, 8);
}

function blankPattern(label = 'Weekly'): WeeklyPattern {
  return {
    id: newPatternId(),
    label,
    days: DAY_LABELS.map((d) => ({ day: d, session: '' })),
  };
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function PhaseEditor({
  phase,
  onCancel,
  onSaved,
  onDeleted,
}: {
  phase?: TrainingPhaseDto;
  onCancel: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
}) {
  const isEdit = !!phase;
  const create = useCreatePhase();
  const patch = usePatchPhase();
  const remove = useDeletePhase();
  const templatesQ = useWorkoutTemplates();
  const templates = templatesQ.data?.templates ?? [];

  // Form state — every field can be edited in either create or edit mode.
  const [templateId, setTemplateId] = useState('blank');
  const [name, setName] = useState(phase?.name ?? '');
  const [intent, setIntent] = useState(phase?.intent ?? '');
  const [startDate, setStartDate] = useState(phase?.startDate ?? todayIso());
  const [endDate, setEndDate] = useState(phase?.endDate ?? addDaysIso(todayIso(), 6 * 7));
  const [weeks, setWeeks] = useState(phase?.weeks ?? 6);
  const [assessAtEnd, setAssessAtEnd] = useState(true);
  const [primaryGoal, setPrimaryGoal] = useState(phase?.primaryGoal ?? '');
  const [secondaryGoal, setSecondaryGoal] = useState(phase?.secondaryGoal ?? '');

  const seedPatterns: WeeklyPattern[] =
    phase?.weeklyPatterns && phase.weeklyPatterns.length > 0
      ? phase.weeklyPatterns
      : [blankPattern()];
  const [patterns, setPatterns] = useState<WeeklyPattern[]>(seedPatterns);
  const seedAssignment: string[] =
    phase?.weekAssignment && phase.weekAssignment.length > 0
      ? phase.weekAssignment
      : Array(phase?.weeks ?? 6).fill(seedPatterns[0]!.id);
  const [assignment, setAssignment] = useState<string[]>(seedAssignment);
  const [activePatternId, setActivePatternId] = useState(seedPatterns[0]!.id);

  // Resize the assignment array whenever the week count changes. Existing
  // values are preserved; new slots default to the first pattern.
  useEffect(() => {
    setAssignment((prev) => {
      const fallback = patterns[0]?.id ?? 'wp-weekly';
      if (weeks > prev.length) return [...prev, ...Array(weeks - prev.length).fill(fallback)];
      if (weeks < prev.length) return prev.slice(0, weeks);
      return prev;
    });
    // intentionally don't depend on patterns identity to avoid pingpong
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks]);

  // Auto-derive endDate when creating a new phase (skip when editing — the
  // user may want manual control).
  useEffect(() => {
    if (!phase) {
      setEndDate(addDaysIso(startDate, weeks * 7 - 1));
    }
  }, [weeks, startDate, phase]);

  function pickTemplate(id: string): void {
    setTemplateId(id);
    const t = PHASE_TEMPLATES.find((x) => x.id === id);
    if (!t) return;
    if (!name) setName(t.name);
    if (!intent) setIntent(t.desc);
    setWeeks(t.weeks);
    // Deep clone so editing doesn't mutate the constant.
    const cloned = t.weeklyPatterns.map((p) => ({
      ...p,
      days: p.days.map((d) => ({ ...d })),
    }));
    setPatterns(cloned);
    setActivePatternId(cloned[0]!.id);
    const tmpl = t.weekAssignment ?? Array(t.weeks).fill(cloned[0]!.id);
    setAssignment(tmpl.slice(0, t.weeks));
    if (t.primaryGoal && !primaryGoal) setPrimaryGoal(t.primaryGoal);
    if (t.secondaryGoal && !secondaryGoal) setSecondaryGoal(t.secondaryGoal);
  }

  function updateDay(
    patternId: string,
    idx: number,
    patch: { session?: string; templateId?: string | null },
  ): void {
    setPatterns((prev) =>
      prev.map((p) =>
        p.id !== patternId
          ? p
          : {
              ...p,
              days: p.days.map((d, i) =>
                i === idx
                  ? {
                      ...d,
                      ...(patch.session !== undefined ? { session: patch.session } : {}),
                      ...(patch.templateId !== undefined
                        ? { templateId: patch.templateId }
                        : {}),
                    }
                  : d,
              ),
            },
      ),
    );
  }
  function updatePatternLabel(patternId: string, label: string): void {
    setPatterns((prev) => prev.map((p) => (p.id === patternId ? { ...p, label } : p)));
  }
  function addPattern(): void {
    if (patterns.length >= 4) return;
    const p = blankPattern(`Pattern ${patterns.length + 1}`);
    setPatterns((prev) => [...prev, p]);
    setActivePatternId(p.id);
  }
  function removePattern(id: string): void {
    if (patterns.length <= 1) return;
    const remaining = patterns.filter((p) => p.id !== id);
    setPatterns(remaining);
    setAssignment((prev) => prev.map((pid) => (pid === id ? remaining[0]!.id : pid)));
    if (activePatternId === id) setActivePatternId(remaining[0]!.id);
  }
  function setWeekAssignment(weekIdx: number, patternId: string): void {
    setAssignment((prev) => prev.map((p, i) => (i === weekIdx ? patternId : p)));
  }

  const activePattern = useMemo(
    () => patterns.find((p) => p.id === activePatternId) ?? patterns[0]!,
    [patterns, activePatternId],
  );
  const multi = patterns.length > 1;
  const ready = name.trim().length > 0 && weeks > 0 && !!startDate && !!endDate;

  async function save(): Promise<void> {
    const payload = {
      name: name.trim(),
      intent,
      startDate,
      endDate,
      weeks,
      primaryGoal: primaryGoal.trim() || null,
      secondaryGoal: secondaryGoal.trim() || null,
      weeklyPatterns: patterns,
      weekAssignment: assignment,
      status: 'active' as const,
    };
    if (isEdit && phase) {
      await patch.mutateAsync({ id: phase.id, patch: payload });
    } else {
      await create.mutateAsync(payload);
    }
    onSaved();
  }

  async function confirmDelete(): Promise<void> {
    if (!phase) return;
    const ok = window.confirm(
      'Delete this phase? Completed and in-progress workouts are kept (they detach from the phase). Planned workouts will be removed.',
    );
    if (!ok) return;
    await remove.mutateAsync(phase.id);
    onDeleted?.();
  }

  const pending = create.isPending || patch.isPending || remove.isPending;

  return (
    <div className={styles.editor}>
      <div className={styles.crumb}>
        <span className={styles.crumbLink} onClick={onCancel}>
          Workouts · Training phase
        </span>
        <Icon name="chevron" size={11} />
        <span className={styles.crumbActive}>{isEdit ? 'Edit phase' : 'New phase'}</span>
      </div>
      <h2 className={styles.editorTitle}>{isEdit ? 'Edit training phase' : 'New training phase'}</h2>
      <div className={styles.editorIntro}>
        A phase is a stretch of training with a clear intent. You can use one weekly plan for the
        whole phase, or define multiple (e.g. build / deload, volume / recovery) and assign them to
        specific weeks.
      </div>

      {!isEdit && (
        <>
          <FieldLabel optional hint="borrow a familiar shape">Template</FieldLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {PHASE_TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`${styles.editorTypeCard} ${templateId === t.id ? styles.editorTypeCardActive : ''}`}
                onClick={() => pickTemplate(t.id)}
              >
                <div className={styles.editorTypeName}>{t.name}</div>
                <div className={styles.editorTypeDesc}>{t.desc}</div>
                {t.weeklyPatterns.length > 1 && (
                  <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 4 }}>
                    {t.weeklyPatterns.length} patterns
                  </div>
                )}
              </button>
            ))}
          </div>
        </>
      )}

      <FieldLabel>Name</FieldLabel>
      <input
        autoFocus={!isEdit}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Recomp + base running · Strength foundation · Marathon block"
        className={`${styles.editorInput} ${styles.editorInputLg}`}
      />

      <FieldLabel hint="why this phase? what would success look like?">Intent</FieldLabel>
      <textarea
        rows={3}
        value={intent}
        onChange={(e) => setIntent(e.target.value)}
        placeholder='e.g. "Body recomposition with steady cardio base. 3 strength sessions + 1 run per week."'
        className={styles.editorInput}
        style={{ minHeight: 80, resize: 'vertical' }}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <FieldLabel>Start date</FieldLabel>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={styles.editorInput}
          />
        </div>
        <div>
          <FieldLabel>End date</FieldLabel>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={styles.editorInput}
          />
        </div>
        <div>
          <FieldLabel>Weeks</FieldLabel>
          <input
            type="number"
            min={1}
            max={26}
            value={weeks}
            onChange={(e) => setWeeks(Math.max(1, Number(e.target.value)))}
            className={styles.editorInput}
          />
        </div>
        <div>
          <FieldLabel>Assessment at end?</FieldLabel>
          <label
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              padding: '9px 12px',
              border: '1px solid var(--line)',
              borderRadius: 6,
              background: 'var(--canvas)',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={assessAtEnd}
              onChange={(e) => setAssessAtEnd(e.target.checked)}
            />
            Schedule a phase assessment session
          </label>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <FieldLabel optional>Primary goal</FieldLabel>
          <input
            value={primaryGoal}
            onChange={(e) => setPrimaryGoal(e.target.value)}
            placeholder="e.g. Body recomposition"
            className={styles.editorInput}
          />
        </div>
        <div>
          <FieldLabel optional>Secondary goal</FieldLabel>
          <input
            value={secondaryGoal}
            onChange={(e) => setSecondaryGoal(e.target.value)}
            placeholder="e.g. Cardiovascular base"
            className={styles.editorInput}
          />
        </div>
      </div>

      {/* Weekly patterns */}
      <FieldLabel hint="one or more weekly plans · assign each phase week to a pattern below">
        Weekly patterns
      </FieldLabel>
      <div className={styles.editorChipRow} style={{ marginBottom: 10 }}>
        {patterns.map((p, i) => (
          <div
            key={p.id}
            className={`${styles.filterChip} ${activePatternId === p.id ? styles.filterChipActive : ''}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, paddingRight: patterns.length > 1 ? 6 : 12 }}
            onClick={() => setActivePatternId(p.id)}
          >
            <span
              style={{ width: 8, height: 8, borderRadius: 2, background: patternColor(i) }}
            />
            <input
              value={p.label}
              onChange={(e) => updatePatternLabel(p.id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder={`Pattern ${i + 1}`}
              style={{
                border: 0,
                background: 'transparent',
                outline: 'none',
                font: 'inherit',
                color: 'inherit',
                width: `${Math.max(6, p.label.length + 1)}ch`,
              }}
            />
            {patterns.length > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removePattern(p.id);
                }}
                style={{
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'inherit',
                  padding: 0,
                  marginLeft: 2,
                }}
              >
                <Icon name="x" size={11} />
              </button>
            )}
          </div>
        ))}
        {patterns.length < 4 && (
          <button
            type="button"
            className={`${styles.filterChip} ${styles.filterChipOther}`}
            onClick={addPattern}
          >
            <Icon name="plus" size={11} /> Add pattern
          </button>
        )}
      </div>

      {/* Active pattern days — each row offers a template picker (dropdown),
          a quick "Rest" option, and a "Custom name…" mode for free text. */}
      <div
        style={{
          border: '1px solid var(--line)',
          borderRadius: 6,
          background: 'var(--canvas)',
        }}
      >
        {activePattern.days.map((d, i) => (
          <DayEditorRow
            key={`${activePattern.id}-${i}`}
            day={d}
            templates={templates}
            onChange={(patch) => updateDay(activePattern.id, i, patch)}
          />
        ))}
      </div>

      {/* Week assignment — only meaningful with multiple patterns */}
      {multi && (
        <>
          <FieldLabel hint="map each phase week to one of the patterns above">
            Week assignment
          </FieldLabel>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 8,
            }}
          >
            {assignment.map((pid, i) => (
              <div
                key={i}
                style={{
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  background: 'var(--canvas)',
                  padding: '8px 10px',
                }}
              >
                <div style={{ fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                  Week {i + 1}
                </div>
                <select
                  value={pid}
                  onChange={(e) => setWeekAssignment(i, e.target.value)}
                  style={{
                    width: '100%',
                    border: '1px solid var(--line)',
                    borderRadius: 4,
                    padding: '4px 6px',
                    background: 'var(--surface-2)',
                    font: 'inherit',
                    fontSize: 13,
                  }}
                >
                  {patterns.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </>
      )}

      <div className={styles.editorFooter}>
        <button type="button" className="kos-btn" onClick={onCancel}>
          Cancel
        </button>
        {isEdit && onDeleted && (
          <button
            type="button"
            className="kos-btn"
            style={{ color: 'var(--neg)' }}
            onClick={() => void confirmDelete()}
            disabled={pending}
          >
            Delete phase
          </button>
        )}
        <span className={styles.editorFooterRight}>
          <span className={styles.editorReady}>
            {ready ? (
              <span style={{ color: 'var(--accent)' }}>● Ready to save</span>
            ) : (
              <span style={{ color: 'var(--ink-4)' }}>○ Name + dates + weeks required</span>
            )}
          </span>
          <button
            type="button"
            className="kos-btn kos-btn-primary"
            disabled={!ready || pending}
            onClick={() => void save()}
          >
            <Icon name="check" size={11} />{' '}
            {isEdit ? 'Save changes' : 'Create phase'}
          </button>
        </span>
      </div>
    </div>
  );
}

function FieldLabel({
  children,
  optional,
  hint,
}: {
  children: ReactNode;
  optional?: boolean | undefined;
  hint?: string | undefined;
}) {
  return (
    <div className={styles.editorLabel}>
      <span className={styles.editorLabelText}>{children}</span>
      {optional && <span className={styles.editorLabelOptional}>OPTIONAL</span>}
      {hint && <span className={styles.editorLabelHint}>{hint}</span>}
    </div>
  );
}

const PATTERN_COLORS = ['#5a7a4a', '#5a7a8a', '#a8843a', '#8a5a8a'];
function patternColor(i: number): string {
  return PATTERN_COLORS[i % PATTERN_COLORS.length]!;
}

// ─── Day editor row ─────────────────────────────────────────────────────

/**
 * One day's editor. Picker modes:
 *
 *   • "template" — a workout template is linked. The row shows the template
 *     name with its type-color dot; clicking "Change" reopens the selector.
 *   • "rest" — session is empty / "Rest". The row shows a muted "Rest day"
 *     label and the same selector to switch to a workout.
 *   • "custom" — free-text session label, no template linked. The selector
 *     can promote a "Custom name…" choice to bring back the text input.
 *
 * The selector itself is a native `<select>` so it's keyboard accessible
 * and works the same on mobile.
 */
function DayEditorRow({
  day,
  templates,
  onChange,
}: {
  day: { day: string; session: string; templateId?: string | null };
  templates: WorkoutTemplateDto[];
  onChange: (patch: { session?: string; templateId?: string | null }) => void;
}) {
  const session = day.session ?? '';
  const isRest = session.trim().toLowerCase().startsWith('rest') || session.trim() === '';
  const linkedTemplate = day.templateId
    ? templates.find((t) => t.id === day.templateId) ?? null
    : null;

  // Three discrete modes the user can switch between via the selector.
  type Mode = 'rest' | 'custom' | 'template';
  const mode: Mode = linkedTemplate ? 'template' : isRest ? 'rest' : 'custom';

  function handleSelect(v: string): void {
    if (v === '__rest__') {
      onChange({ session: 'Rest', templateId: null });
      return;
    }
    if (v === '__custom__') {
      // Switch to custom-name mode. Leave the session text alone if it
      // already had something; otherwise blank so the input is ready.
      onChange({
        session: !isRest && !linkedTemplate ? session : '',
        templateId: null,
      });
      return;
    }
    // Template id.
    const t = templates.find((x) => x.id === v);
    if (!t) return;
    onChange({ session: t.name, templateId: t.id });
  }

  const selectValue =
    mode === 'rest' ? '__rest__' : mode === 'template' ? linkedTemplate!.id : '__custom__';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '60px 1fr',
        gap: 12,
        alignItems: 'center',
        padding: '8px 12px',
        borderBottom: '1px solid var(--line-2, var(--line))',
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-3)' }}>
        {day.day}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <select
          value={selectValue}
          onChange={(e) => handleSelect(e.target.value)}
          style={{
            flex: '0 0 auto',
            minWidth: 180,
            padding: '6px 8px',
            border: '1px solid var(--line)',
            borderRadius: 4,
            background: 'var(--surface-2)',
            font: 'inherit',
            fontSize: 14,
          }}
        >
          <option value="__rest__">— Rest day —</option>
          {templates.length > 0 && (
            <optgroup label="Workout templates">
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </optgroup>
          )}
          <option value="__custom__">Custom name…</option>
        </select>

        {mode === 'template' && linkedTemplate && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--ink-3)', fontSize: 13, minWidth: 0 }}>
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: 2,
                background:
                  (linkedTemplate.type in EXERCISE_TYPE_META
                    ? EXERCISE_TYPE_META[
                        linkedTemplate.type as keyof typeof EXERCISE_TYPE_META
                      ].color
                    : 'var(--ink-4)'),
              }}
            />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {linkedTemplate.name}
            </span>
            {linkedTemplate.durationMin && (
              <span style={{ color: 'var(--ink-4)' }}>· {linkedTemplate.durationMin} min</span>
            )}
          </span>
        )}

        {mode === 'rest' && (
          <span style={{ color: 'var(--ink-4)', fontSize: 13, fontStyle: 'italic' }}>
            no session generated
          </span>
        )}

        {mode === 'custom' && (
          <input
            value={session}
            onChange={(e) => onChange({ session: e.target.value, templateId: null })}
            placeholder="e.g. Long easy run · Mobility · Track session…"
            style={{
              flex: 1,
              minWidth: 100,
              border: '1px solid var(--line)',
              borderRadius: 4,
              padding: '6px 8px',
              background: 'var(--canvas)',
              font: 'inherit',
              fontSize: 14,
              outline: 'none',
            }}
          />
        )}
      </div>
    </div>
  );
}
